import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  FiAlertCircle, FiEye, FiTrash2,
  FiX, FiSearch, FiRefreshCw, FiChevronLeft, FiChevronRight,
  FiSend, FiFilter, FiMessageSquare
} from "react-icons/fi";
import {
  fetchComplaints as apiFetchComplaints,
  updateComplaints,
  replyComplaints,
  deleteComplaints,
} from "../api/complaintsApi";

const CATEGORIES = [
  { value: "",                label: "All Categories" },
  { value: "driver_behaviour",label: "Driver Behaviour" },
  { value: "ride_issue",      label: "Ride Issue" },
  { value: "payment_issue",   label: "Payment Issue" },
  { value: "app_issue",       label: "App Issue" },
  { value: "safety",          label: "Safety" },
  { value: "lost_item",       label: "Lost Item" },
  { value: "overcharging",    label: "Overcharging" },
  { value: "route_issue",     label: "Route Issue" },
  { value: "other",           label: "Other" },
];

const STATUSES = [
  { value: "",          label: "All Status" },
  { value: "pending",   label: "Pending",   color: "#B45309", bg: "#FEF3C7" },
  { value: "in_review", label: "In Review", color: "#1D4ED8", bg: "#DBEAFE" },
  { value: "resolved",  label: "Resolved",  color: "#065F46", bg: "#D1FAE5" },
  { value: "closed",    label: "Closed",    color: "#374151", bg: "#F3F4F6" },
  { value: "rejected",  label: "Rejected",  color: "#991B1B", bg: "#FEE2E2" },
];

const PRIORITIES = [
  { value: "",       label: "All Priority" },
  { value: "low",    label: "Low",    color: "#6B7280", bg: "#F9FAFB" },
  { value: "normal", label: "Normal", color: "#1D4ED8", bg: "#EFF6FF" },
  { value: "high",   label: "High",   color: "#D97706", bg: "#FFFBEB" },
  { value: "urgent", label: "Urgent", color: "#DC2626", bg: "#FEF2F2" },
];

const getStatus = v => STATUSES.find(s => s.value === v) || STATUSES[0];
const getPriority = v => PRIORITIES.find(p => p.value === v) || PRIORITIES[1];
const getCategoryLabel = v => CATEGORIES.find(c => c.value === v)?.label || v;

const formatDate = d => {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

// ── Styles ────────────────────────────────────────────────────────────────
const S = {
  page:     { padding: "28px 32px", fontFamily: "'Inter','Segoe UI',sans-serif", background: "#F7F8FA", minHeight: "100vh" },
  header:   { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 },
  title:    { fontSize: 22, fontWeight: 700, color: "#111827", display: "flex", alignItems: "center", gap: 10 },
  subtitle: { fontSize: 13, color: "#6B7280", marginTop: 3 },

  statsRow: { display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" },
  statCard: (color) => ({
    background: "#fff", border: `1.5px solid ${color}22`, borderRadius: 12,
    padding: "14px 20px", flex: "1 1 110px", minWidth: 110,
  }),
  statNum:   (color) => ({ fontSize: 26, fontWeight: 800, color }),
  statLabel: { fontSize: 12, color: "#9CA3AF", marginTop: 2 },

  card:      { background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", padding: "16px 20px", marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" },
  filterRow: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 },
  select:    { border: "1px solid #D1D5DB", borderRadius: 8, padding: "8px 12px", fontSize: 13, background: "#fff", cursor: "pointer", outline: "none" },
  input:     { border: "1px solid #D1D5DB", borderRadius: 8, padding: "8px 12px", fontSize: 13, background: "#fff", outline: "none" },
  searchBox: { display: "flex", alignItems: "center", border: "1px solid #D1D5DB", borderRadius: 8, background: "#fff", padding: "0 12px", gap: 8, flex: "1 1 220px" },

  btnPrimary: { background: "#1d9e75", color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 },
  btnGhost:   { background: "transparent", color: "#6B7280", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 14px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 },
  btnDanger:  { background: "#FEE2E2", color: "#DC2626", border: "none", borderRadius: 6, padding: "5px 9px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 },
  btnIcon:    { background: "#EFF6FF", color: "#1D4ED8", border: "none", borderRadius: 6, padding: "5px 9px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 },

  table:  { width: "100%", borderCollapse: "collapse" },
  th:     { textAlign: "left", padding: "11px 14px", fontSize: 12, fontWeight: 700, color: "#6B7280", borderBottom: "2px solid #F3F4F6", whiteSpace: "nowrap" },
  td:     { padding: "12px 14px", fontSize: 13, color: "#374151", borderBottom: "1px solid #F9FAFB", verticalAlign: "middle" },
  badge:  (color, bg) => ({ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, color, background: bg }),
  pagination: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, fontSize: 13, color: "#6B7280" },
  pageBtn: (active) => ({
    padding: "6px 12px", borderRadius: 6, border: "1px solid #E5E7EB", background: active ? "#1d9e75" : "#fff",
    color: active ? "#fff" : "#374151", cursor: "pointer", fontWeight: active ? 700 : 400,
  }),

  // Modal
  overlay:  { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  modal:    { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 680, maxHeight: "90vh", overflowY: "auto", padding: "28px 30px", position: "relative" },
  modalTitle: { fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 20 },
  label:    { fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4, display: "block" },
  textarea: { border: "1px solid #D1D5DB", borderRadius: 8, padding: "8px 12px", fontSize: 13, resize: "vertical", fontFamily: "inherit", width: "100%", outline: "none", boxSizing: "border-box" },
  divider:  { border: "none", borderTop: "1px solid #F3F4F6", margin: "16px 0" },

  replyBubble: { background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "10px 14px", marginBottom: 10 },
  replyMeta:   { fontSize: 11, color: "#6B7280", marginTop: 6 },
  infoRow:     { display: "flex", gap: 8, marginBottom: 10, alignItems: "flex-start" },
  infoLabel:   { minWidth: 130, fontSize: 12, fontWeight: 600, color: "#9CA3AF" },
  infoValue:   { fontSize: 13, color: "#374151", flex: 1 },
};

// ════════════════════════════════════════════════════════════════════════════
// 🎛️ MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
export default function ComplaintsPage() {

  const [complaints, setComplaints]   = useState([]);
  const [stats, setStats]             = useState({});
  const [loading, setLoading]         = useState(false);
  const [pagination, setPagination]   = useState({ currentPage: 1, totalPages: 1, totalCount: 0 });
  const [selected, setSelected]       = useState(null);   // detail modal
  const [replyText, setReplyText]     = useState("");
  const [replying, setReplying]       = useState(false);
  const [updating, setUpdating]       = useState(false);
  const [adminNote, setAdminNote]     = useState("");
  const [resNote, setResNote]         = useState("");

  // ── Filters ───────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    c_status: "", c_category: "", c_priority: "",
    c_search_term: "", page: 1,
  });

const filtersRef = useRef(filters);

  // ════════════════════════════════════════════════════════════════════════
  // 🔄 FETCH
  // ════════════════════════════════════════════════════════════════════════
//   const fetchComplaints = useCallback(async (f = filters) => {
//     setLoading(true);
//     try {
//       const data = await apiFetchComplaints({ ...f, n_page: f.page, n_limit: 10 });
//       if (data.success) {
//         setComplaints(data.complaints);
//         setStats(data.stats || {});
//         setPagination(data.pagination || {});
//       }
//     } catch (err) {
//       console.error("fetchComplaints error:", err);
//     } finally {
//       setLoading(false);
//     }
//   }, [filters]);

const fetchComplaints = useCallback(async (f) => {
  const activeFilters = f || filtersRef.current;
  setLoading(true);
  try {
    const data = await apiFetchComplaints({ ...activeFilters, n_page: activeFilters.page, n_limit: 10 });
    if (data.success) {
      setComplaints(data.complaints);
      setStats(data.stats || {});
      setPagination(data.pagination || {});
    }
  } catch (err) {
    console.error("fetchComplaints error:", err);
  } finally {
    setLoading(false);
  }
}, []); // ✅ empty dependency — no circular issue

useEffect(() => { filtersRef.current = filters; }, [filters]);

  const applyFilters = () => fetchComplaints({ ...filters, page: 1 });

  const changePage = (p) => {
    const updated = { ...filters, page: p };
    setFilters(updated);
    fetchComplaints(updated);
  };

  // ════════════════════════════════════════════════════════════════════════
  // ✏️ STATUS UPDATE
  // ════════════════════════════════════════════════════════════════════════
  const handleStatusUpdate = async (id, status, extra = {}) => {
    setUpdating(true);
    try {
      const data = await updateComplaints(id, { status, ...extra });
      if (data.success) {
        setSelected(data.complaint);
        fetchComplaints();
      }
    } finally {
      setUpdating(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // 💬 ADD REPLY
  // ════════════════════════════════════════════════════════════════════════
  const handleReply = async () => {
    if (!replyText.trim() || !selected) return;
    setReplying(true);
    try {
      const data = await replyComplaints(selected.complaintId, {
        message: replyText.trim(),
        repliedBy: "Admin",
      });
      if (data.success) {
        setSelected(data.complaint);
        setReplyText("");
        fetchComplaints();
      }
    } finally {
      setReplying(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // 🗑️ DELETE
  // ════════════════════════════════════════════════════════════════════════
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this complaint?")) return;
    await deleteComplaints(id);
    fetchComplaints();
    if (selected?.complaintId === id) setSelected(null);
  };

  // ════════════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={S.page}>
      {/* ── Header ── */}
      <div style={S.header}>
        <div>
          <div style={S.title}>
            <FiAlertCircle size={22} color="#DC2626" />
            Complaints Management
          </div>
          <div style={S.subtitle}>Manage and resolve passenger complaints</div>
        </div>
        <button style={S.btnGhost} onClick={() => fetchComplaints()}>
          <FiRefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* ── Stats ── */}
      <div style={S.statsRow}>
        {[
          { label: "Total",     val: stats.total,     color: "#374151" },
          { label: "Pending",   val: stats.pending,   color: "#D97706" },
          { label: "In Review", val: stats.in_review, color: "#1D4ED8" },
          { label: "Resolved",  val: stats.resolved,  color: "#065F46" },
          { label: "Closed",    val: stats.closed,    color: "#6B7280" },
          { label: "Urgent",    val: stats.urgent,    color: "#DC2626" },
        ].map(s => (
          <div key={s.label} style={S.statCard(s.color)}>
            <div style={S.statNum(s.color)}>{s.val ?? 0}</div>
            <div style={S.statLabel}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div style={S.card}>
        <div style={S.filterRow}>
          <div style={S.searchBox}>
            <FiSearch size={14} color="#9CA3AF" />
            <input
              style={{ border: "none", outline: "none", fontSize: 13, flex: 1, padding: "8px 0" }}
              placeholder="Search by ID, name, subject…"
              value={filters.c_search_term}
              onChange={e => setFilters({ ...filters, c_search_term: e.target.value })}
              onKeyDown={e => e.key === "Enter" && applyFilters()}
            />
          </div>
          <select style={S.select} value={filters.c_status} onChange={e => setFilters({ ...filters, c_status: e.target.value })}>
            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select style={S.select} value={filters.c_category} onChange={e => setFilters({ ...filters, c_category: e.target.value })}>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <select style={S.select} value={filters.c_priority} onChange={e => setFilters({ ...filters, c_priority: e.target.value })}>
            {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <button style={S.btnPrimary} onClick={applyFilters}>
            <FiFilter size={13} /> Apply
          </button>
          <button style={S.btnGhost} onClick={() => {
            const reset = { c_status: "", c_category: "", c_priority: "", c_search_term: "", page: 1 };
            setFilters(reset);
            fetchComplaints(reset);
          }}>
            <FiX size={13} /> Clear
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div style={S.card}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}>Loading complaints…</div>
        ) : complaints.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}>No complaints found</div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                {["Complaint ID", "Passenger", "Category", "Subject", "Status", "Priority", "Date", "Actions"].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {complaints.map(c => {
                const st = getStatus(c.status);
                const pr = getPriority(c.priority);
                return (
                  <tr key={c._id} style={{ transition: "background .15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                    onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <td style={S.td}>
                      <span style={{ fontFamily: "monospace", fontSize: 12, color: "#374151" }}>{c.complaintId}</span>
                    </td>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600 }}>{c.passengerName || c.passengerId}</div>
                      {c.passengerPhone && <div style={{ fontSize: 11, color: "#9CA3AF" }}>{c.passengerPhone}</div>}
                    </td>
                    <td style={S.td}>{getCategoryLabel(c.category)}</td>
                    <td style={{ ...S.td, maxWidth: 180 }}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.subject}</div>
                      {c.replies?.length > 0 && (
                        <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>
                          <FiMessageSquare size={10} style={{ marginRight: 3 }} />{c.replies.length} repl{c.replies.length === 1 ? "y" : "ies"}
                        </div>
                      )}
                    </td>
                    <td style={S.td}>
                      <span style={S.badge(st.color, st.bg)}>{st.label}</span>
                    </td>
                    <td style={S.td}>
                      <span style={S.badge(pr.color, pr.bg)}>{pr.label}</span>
                    </td>
                    <td style={{ ...S.td, whiteSpace: "nowrap", fontSize: 12 }}>
                      {formatDate(c.createdAt)}
                    </td>
                    <td style={S.td}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button style={S.btnIcon} title="View" onClick={() => { setSelected(c); setAdminNote(c.adminNote || ""); setResNote(c.resolutionNote || ""); }}>
                          <FiEye size={13} />
                        </button>
                        <button style={S.btnDanger} title="Delete" onClick={() => handleDelete(c.complaintId)}>
                          <FiTrash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* ── Pagination ── */}
        {pagination.totalPages > 1 && (
          <div style={S.pagination}>
            <span>Showing page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalCount} total)</span>
            <div style={{ display: "flex", gap: 6 }}>
              <button style={S.btnGhost} disabled={!pagination.hasPrevPage} onClick={() => changePage(pagination.currentPage - 1)}>
                <FiChevronLeft size={14} />
              </button>
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => i + 1).map(p => (
                <button key={p} style={S.pageBtn(p === pagination.currentPage)} onClick={() => changePage(p)}>{p}</button>
              ))}
              <button style={S.btnGhost} disabled={!pagination.hasNextPage} onClick={() => changePage(pagination.currentPage + 1)}>
                <FiChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
           DETAIL MODAL
      ══════════════════════════════════════════════════════════════════ */}
      {selected && (
        <div style={S.overlay} onClick={() => setSelected(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={S.modalTitle}>
                <FiAlertCircle style={{ marginRight: 8, color: "#DC2626" }} />
                {selected.complaintId}
              </div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF" }} onClick={() => setSelected(null)}>
                <FiX size={20} />
              </button>
            </div>

            {/* Info grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
              {[
                ["Passenger",   selected.passengerName || selected.passengerId],
                ["Phone",       selected.passengerPhone || "—"],
                ["Category",    getCategoryLabel(selected.category)],
                ["Ride ID",     selected.rideId || "—"],
                ["Filed On",    formatDate(selected.createdAt)],
                ["Resolved At", formatDate(selected.resolvedAt)],
              ].map(([k, v]) => (
                <div key={k} style={S.infoRow}>
                  <span style={S.infoLabel}>{k}</span>
                  <span style={S.infoValue}>{v}</span>
                </div>
              ))}
            </div>

            {/* Badges */}
            <div style={{ display: "flex", gap: 10, margin: "10px 0 16px" }}>
              <span style={S.badge(getStatus(selected.status).color, getStatus(selected.status).bg)}>
                {getStatus(selected.status).label}
              </span>
              <span style={S.badge(getPriority(selected.priority).color, getPriority(selected.priority).bg)}>
                {getPriority(selected.priority).label}
              </span>
              {selected.satisfactionRating && (
                <span style={S.badge("#B45309", "#FEF3C7")}>⭐ {selected.satisfactionRating}/5</span>
              )}
            </div>

            <hr style={S.divider} />

            {/* Subject & Description */}
            <label style={S.label}>Subject</label>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#111827", marginBottom: 10 }}>{selected.subject}</p>
            <label style={S.label}>Description</label>
            <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, marginBottom: 16 }}>{selected.description}</p>

            <hr style={S.divider} />

            {/* Replies */}
            <label style={{ ...S.label, marginBottom: 10 }}>
              Replies ({selected.replies?.length || 0})
            </label>
            {selected.replies?.length > 0 ? (
              selected.replies.map((r, i) => (
                <div key={i} style={S.replyBubble}>
                  <div style={{ fontSize: 13, color: "#065F46" }}>{r.message}</div>
                  <div style={S.replyMeta}>by {r.repliedBy} • {formatDate(r.repliedAt)}</div>
                </div>
              ))
            ) : (
              <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 12 }}>No replies yet</div>
            )}

            {/* Reply box */}
            <div style={{ marginTop: 12 }}>
              <textarea
                style={{ ...S.textarea, marginBottom: 8 }}
                rows={3}
                placeholder="Write a reply to the passenger…"
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
              />
              <button style={S.btnPrimary} onClick={handleReply} disabled={replying || !replyText.trim()}>
                <FiSend size={13} /> {replying ? "Sending…" : "Send Reply"}
              </button>
            </div>

            <hr style={S.divider} />

            {/* Status update */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={S.label}>Update Status</label>
                <select
                  style={{ ...S.select, width: "100%" }}
                  value={selected.status}
                  onChange={e => handleStatusUpdate(selected.complaintId, e.target.value)}
                  disabled={updating}
                >
                  {STATUSES.filter(s => s.value).map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={S.label}>Update Priority</label>
                <select
                  style={{ ...S.select, width: "100%" }}
                  value={selected.priority}
                  onChange={e => handleStatusUpdate(selected.complaintId, selected.status, { priority: e.target.value })}
                  disabled={updating}
                >
                  {PRIORITIES.filter(p => p.value).map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Admin note */}
            <label style={S.label}>Admin Note (internal)</label>
            <textarea style={{ ...S.textarea, marginBottom: 8 }} rows={2}
              placeholder="Internal note — not visible to passenger"
              value={adminNote} onChange={e => setAdminNote(e.target.value)} />

            {/* Resolution note */}
            <label style={{ ...S.label, marginTop: 8 }}>Resolution Note (visible to passenger)</label>
            <textarea style={{ ...S.textarea, marginBottom: 12 }} rows={2}
              placeholder="Explain how this was resolved"
              value={resNote} onChange={e => setResNote(e.target.value)} />

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button style={S.btnGhost} onClick={() => setSelected(null)}>Close</button>
              <button style={S.btnPrimary} disabled={updating}
                onClick={() => handleStatusUpdate(selected.complaintId, selected.status, { adminNote, resolutionNote: resNote })}>
                {updating ? "Saving…" : "Save Notes"}
              </button>
              <button style={S.btnDanger} onClick={() => handleDelete(selected.complaintId)}>
                <FiTrash2 size={13} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}